import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'

// Import order is cascade order. main.js imported App.vue — whose global styles
// are now app-globals.css — BEFORE the Vuetify plugin and main.css, so the app's
// overrides in main.css (.v-application's font among them) come last and win.
// Keep exactly that order here; only the tokens and Tailwind, which emit
// variables and layered utilities rather than plain rules, go first. Reordering
// these once cost two visual baselines.
import '@/design/tailwind.css'
import '@/design/app-globals.css'
import '@/core/plugins/vuetify'
import 'leaflet/dist/leaflet.css'
import '@/assets/styles/main.css'

import { queryClient } from '@/core/cache/queryClient'
import { bootstrapSession } from '@/core/session/bootstrapSession'
import { createAppRouter } from '@/react/router/router'
import { initGA } from '@/shared/utils/analytics'

/**
 * React owns #app and the URL. The root route renders the frame; the map route
 * mounts the Vue island (src/islands/MapIsland.vue) through the registry. Each
 * Phase 1 sub-plan pulls another piece out of the island; each wave replaces an
 * island with a React implementation.
 */

// Before the router reads the URL, exactly where main.js ran it: ahead of
// vue-router's first guard. It may strip ?demo= and ?tier= from the location.
bootstrapSession()

const router = createAppRouter()

const container = document.getElementById('app')
if (!container) {
  throw new Error('#app is missing from index.html')
}

const root = createRoot(container, {
  onUncaughtError: (error, info) => {
    console.error('[react] uncaught render error', error, info.componentStack)
  },
  onCaughtError: (error, info) => {
    console.error('[react] error caught by a boundary', error, info.componentStack)
  },
})

root.render(
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
  </QueryClientProvider>,
)

initGA()
